/* Metta mega menu — play a card's video only while its panel is open.

   The markup deliberately leaves videos as preload="none" with no autoplay
   attribute: three panels' worth of video would otherwise be fetched and
   decoded on every page load for menus most visitors never open. theme.js
   opens a panel by adding `.active` to it, so that class is the cue. */
(() => {
  const panels = document.querySelectorAll('.metta-meganav');
  if (!panels.length) return;

  const sync = (panel) => {
    const open = panel.classList.contains('active');

    panel.querySelectorAll('.metta-meganav__card-video').forEach((video) => {
      if (open) {
        // preload="none" means there is nothing buffered until the first open.
        video.play().catch(() => {
          /* autoplay refused (reduced data, power saving) — the poster stands in */
        });
      } else {
        video.pause();
      }
    });
  };

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => sync(mutation.target));
  });

  panels.forEach((panel) => {
    observer.observe(panel, { attributes: true, attributeFilter: ['class'] });
  });
})();

/* The page's scrollbar width, for custom.css to pad the locked page by
   (--metta-scrollbar). Measured only while nothing is open, when the bar
   is there to measure. */
(() => {
  const root = document.documentElement;
  const measure = () => {
    if (document.body.getAttribute('data-fluorescent-overlay-open') === 'true') return;
    if (document.querySelector('dialog[open]')) return;
    root.style.setProperty('--metta-scrollbar', `${Math.max(0, window.innerWidth - root.clientWidth)}px`);
  };
  measure();
  window.addEventListener('resize', measure);
})();

/* Metta phone menu motion, Oner's timeline (eu.oneractive.com at 390,
   measured frame by frame 21 Oct 2026; their menu runs on GSAP):
   - Open: the sheet unrolls from the header, 400ms, fast then settling.
     The tabs fade in one after the other from 180ms (200 each, 50 apart),
     the chosen tab's line grows from its left edge at 310ms (350), and the
     menu's contents slide in 20px from the left and fade in at 480ms (300).
   - Close: the contents slide 10px left and fade, bottom block first; the
     line shrinks; then the sheet rolls back up into the header. It all ends
     inside the theme's 400ms before it hides the drawer.
   - Konur / Karlar: Oner's tab switch is a hard cut and a 20px slide-in
     (240ms), but Oner's tabs hold different photos and ours hold the same
     words, so on ours that read as a twitch (22 Oct 2026). The old list
     steps out (10px, 120ms), then the new rows slide in one after another
     (300ms each, 20ms apart). The line grows under the new tab (350ms).
   - Groups: one open at a time. Opening grows the group (200ms) and then
     slides its links in (100ms); closing slides them out (150ms) and then
     folds the group (200ms).
   Reduced motion: everything snaps. Curves are GSAP's power eases. */
const mettaMenuEase = {
  out2: 'cubic-bezier(0.5, 1, 0.89, 1)', // power1.out (quad)
  out3: 'cubic-bezier(0.33, 1, 0.68, 1)', // power2.out (cubic)
  out4: 'cubic-bezier(0.25, 1, 0.5, 1)', // power3.out (quart)
  in2: 'cubic-bezier(0.11, 0, 0.5, 0)', // power1.in
  in3: 'cubic-bezier(0.32, 0, 0.67, 0)', // power2.in
};
const mettaMenuStill = window.matchMedia('(prefers-reduced-motion: reduce)');
const mettaMenuPhone = window.matchMedia('(max-width: 959px)');

/* Tabs. The tab of the page being viewed opens first (set in Liquid) and is
   remembered; elsewhere the last tab chosen or browsed this visit. Arrow
   keys move between tabs. */
(() => {
  const nav = document.querySelector('[data-metta-dnav]');
  if (!nav) return;
  const tabs = Array.from(nav.querySelectorAll('[role="tab"]'));
  const bar = nav.querySelector('[role="tablist"]');
  const ink = nav.querySelector('.metta-dnav__ink');
  const KEY = 'metta-dnav-tab';

  const panels = Array.from(nav.querySelectorAll('[data-panel]'));
  const show = (tab) => panels.forEach((p) => { p.hidden = p.dataset.panel !== tab.dataset.tab; });
  const rows = (panel) => Array.from(panel.querySelectorAll(
    ':scope > .metta-dnav__big > li, :scope > .metta-dnav__groups > *',
  ));
  let moves = [];

  const select = (tab, focus, animate) => {
    const from = tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
    const to = tabs.indexOf(tab);
    tabs.forEach((t) => {
      const on = t === tab;
      t.setAttribute('aria-selected', String(on));
      t.tabIndex = on ? 0 : -1;
    });
    if (bar) bar.style.setProperty('--i', String(to));
    // A tap mid-switch: the last one wins, from wherever the lists are.
    moves.forEach((a) => a.cancel());
    moves = [];
    const old = panels.find((p) => !p.hidden);
    const panel = nav.querySelector(`[data-panel="${tab.dataset.tab}"]`);
    if (animate && from !== to && old && panel && !mettaMenuStill.matches && Element.prototype.animate) {
      if (ink) {
        moves.push(ink.animate([{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }], {
          duration: 350,
          easing: mettaMenuEase.out3,
        }));
      }
      const out = old.animate(
        [{ transform: 'none', opacity: 1 }, { transform: 'translateX(-10px)', opacity: 0 }],
        { duration: 120, easing: mettaMenuEase.in3, fill: 'forwards' },
      );
      moves.push(out);
      out.onfinish = () => {
        show(tab);
        out.cancel();
        const list = rows(panel);
        const delay = (i) => Math.min(i, 11) * 20;
        list.forEach((row, i) => {
          moves.push(row.animate(
            [{ transform: 'translateX(-20px)', opacity: 0 }, { transform: 'none', opacity: 1 }],
            { duration: 300, delay: delay(i), easing: mettaMenuEase.out3, fill: 'backwards' },
          ));
        });
        // The rule above the groups arrives with the first group, not before.
        const groups = panel.querySelector('.metta-dnav__groups');
        const first = groups ? list.indexOf(groups.firstElementChild) : -1;
        if (first >= 0) {
          moves.push(groups.animate(
            [{ borderTopColor: 'transparent' }, { borderTopColor: getComputedStyle(groups).borderTopColor }],
            { duration: 300, delay: delay(first), easing: mettaMenuEase.out3, fill: 'backwards' },
          ));
        }
      };
    } else {
      show(tab);
    }
    if (focus) tab.focus();
    try { sessionStorage.setItem(KEY, tab.dataset.tab); } catch (e) { /* storage off */ }
  };

  tabs.forEach((tab, i) => {
    tab.addEventListener('click', () => select(tab, false, true));
    tab.addEventListener('keydown', (e) => {
      const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
      if (!step) return;
      e.preventDefault();
      select(tabs[(i + step + tabs.length) % tabs.length], true, true);
    });
  });

  // A Karlar page opens (and remembers) Karlar; elsewhere the last tab wins.
  if (nav.hasAttribute('data-from-page')) {
    select(tabs.find((t) => t.getAttribute('aria-selected') === 'true') || tabs[0]);
  } else {
    let saved = null;
    try { saved = sessionStorage.getItem(KEY); } catch (e) { /* storage off */ }
    const tab = tabs.find((t) => t.dataset.tab === saved);
    if (tab) select(tab);
  }
})();

/* Open and close. theme.js adds .active to open (and .visible 50ms on),
   and on close drops .visible, then .active 400ms later; the drawer's
   classes are the cue, so theme.js keeps its scroll lock and focus trap. */
(() => {
  const drawer = document.querySelector('[data-drawer-menu]');
  const nav = drawer && drawer.querySelector('[data-metta-dnav]');
  if (!nav || !Element.prototype.animate) return;
  const panel = drawer.querySelector('.drawer-menu__panel');
  const tabs = Array.from(nav.querySelectorAll('[role="tab"]'));
  const ink = nav.querySelector('.metta-dnav__ink');
  // The tabs stay put; everything under them moves as one list, as on Oner.
  const blocks = () => Array.from(nav.children).filter(
    (el) => !el.matches('[role="tablist"]') && !el.hidden,
  );
  let running = [];
  let wasActive = drawer.classList.contains('active');
  let wasVisible = drawer.classList.contains('visible');

  const play = (el, frames, options) => {
    const a = el.animate(frames, { fill: 'backwards', ...options });
    running.push(a);
    return a;
  };
  const stop = () => {
    running.forEach((a) => a.cancel());
    running = [];
  };
  const sheet = () => {
    const top = parseFloat(getComputedStyle(panel).paddingTop) || 0;
    return `inset(0px 0px ${Math.max(panel.offsetHeight - top, 0)}px 0px)`;
  };
  const moving = () => mettaMenuPhone.matches && !mettaMenuStill.matches;

  const open = () => {
    stop();
    if (!moving()) return;
    play(panel, [{ clipPath: sheet() }, { clipPath: 'inset(0px)' }], { duration: 400, easing: mettaMenuEase.out3 });
    tabs.forEach((tab, i) => {
      play(tab, [{ opacity: 0 }, { opacity: 1 }], { duration: 200, delay: 180 + i * 50, easing: mettaMenuEase.out2 });
    });
    if (ink) {
      play(ink, [{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }], { duration: 350, delay: 310, easing: mettaMenuEase.out3 });
    }
    blocks().forEach((el) => {
      play(el, [{ transform: 'translateX(-20px)', opacity: 0 }, { transform: 'none', opacity: 1 }], {
        duration: 300,
        delay: 480,
        easing: mettaMenuEase.out3,
      });
    });
  };

  const close = () => {
    stop();
    if (!moving()) return;
    // 'both': hold the open state through each delay (the panel waits 140),
    // or the closed state shows first and the panel blinks (2 Nov 2026).
    const out = { fill: 'both' };
    blocks().reverse().forEach((el, i) => {
      play(el, [{ transform: 'none', opacity: 1 }, { transform: 'translateX(-10px)', opacity: 0 }], {
        ...out, duration: 110, delay: i * 25, easing: mettaMenuEase.in3,
      });
    });
    if (ink) {
      play(ink, [{ transform: 'scaleX(1)' }, { transform: 'scaleX(0)' }], { ...out, duration: 110, easing: mettaMenuEase.in3 });
    }
    play(panel, [{ clipPath: 'inset(0px)' }, { clipPath: sheet() }], {
      ...out, duration: 210, delay: 140, easing: mettaMenuEase.in2, // done by 350
    });
  };

  new MutationObserver(() => {
    const active = drawer.classList.contains('active');
    const visible = drawer.classList.contains('visible');
    if (active && !wasActive) open();
    else if (active && wasVisible && !visible) close();
    else if (!active && wasActive) stop(); // hidden: back to rest for next time
    wasActive = active;
    wasVisible = visible;
  }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
})();

/* Search and cart move as the menu (27 Oct 2026, Pétur: "search og cart
   animation eins og hamborgarinn"). Same timeline as the menu above: the
   panel arrives over 400ms, its top row (title, field, cart header) fades
   in from 180ms, 50 apart, and the rest slides in 20px from the left and
   fades in at 480ms (300). Closing, the rest slides 10px left and fades,
   last block first, then the panel goes.
   - Search: on phones the panel unrolls from the header, as the menu,
     and rolls up over 210ms after 140. On desktop (30 Oct 2026, Pétur:
     "search sama animation og cart") the drawer comes in from the right
     as the cart's (metta-motion.css). The theme hides it at 350, as the cart.
   - Cart (every width): keeps its own path (the sheet rises from the foot
     on phones, the drawer comes in from the right on desktop), on the
     menu's curves; metta-cart.css carries the travel, this the contents.
   Reduced motion: nothing moves. */
const mettaPanelMotion = (root, { panel, lead, rest, isOpen, isShown, reveal }) => {
  let running = [];
  let wasOpen = isOpen();
  let wasShown = isShown();
  const play = (el, frames, options) => {
    const a = el.animate(frames, { fill: 'backwards', ...options });
    running.push(a);
  };
  const stop = () => {
    running.forEach((a) => a.cancel());
    running = [];
  };
  const shown = (els) => els.filter((el) => el && el.offsetHeight);

  const open = () => {
    stop();
    if (mettaMenuStill.matches || !reveal.on()) return;
    const unroll = reveal.open && reveal.open();
    if (unroll) play(panel, unroll, { duration: 400, easing: mettaMenuEase.out3 });
    shown(lead()).forEach((el, i) => {
      play(el, [{ opacity: 0 }, { opacity: 1 }], { duration: 200, delay: 180 + i * 50, easing: mettaMenuEase.out2 });
    });
    shown(rest()).forEach((el) => {
      play(el, [{ transform: 'translateX(-20px)', opacity: 0 }, { transform: 'none', opacity: 1 }], {
        duration: 300, delay: 480, easing: mettaMenuEase.out3,
      });
    });
  };

  const close = () => {
    stop();
    if (mettaMenuStill.matches || !reveal.on()) return;
    // 'both': hold the open state through each delay (the panel waits 140),
    // or the closed state shows first and the panel blinks (2 Nov 2026).
    const out = { fill: 'both' };
    shown(rest()).reverse().forEach((el, i) => {
      play(el, [{ transform: 'none', opacity: 1 }, { transform: 'translateX(-10px)', opacity: 0 }], {
        ...out, duration: 110, delay: Math.min(i, 3) * 25, easing: mettaMenuEase.in3,
      });
    });
    const rollUp = reveal.close && reveal.close();
    if (rollUp) play(panel, rollUp, { ...out, duration: 210, delay: 140, easing: mettaMenuEase.in2 });
  };

  new MutationObserver(() => {
    const opened = isOpen();
    const visible = isShown();
    if (visible && !wasShown) open();
    else if (wasShown && !visible) close();
    else if (!opened && wasOpen) stop(); // hidden: back to rest for next time
    wasOpen = opened;
    wasShown = visible;
  }).observe(root, { attributes: true, attributeFilter: ['class'] });
};

(() => {
  const search = document.querySelector('[data-quick-search]');
  const panel = search && search.querySelector('.quick-search__container');
  if (!panel || !Element.prototype.animate) return;
  const form = panel.querySelector('.quick-search__form');
  const bar = panel.querySelector('.quick-search__bar');
  // The theme's closed panel is see-through and lifted (no transition, as
  // metta-motion.css set it); held in place while it rolls up.
  const sheet = () => `inset(0px 0px ${panel.offsetHeight}px 0px)`;
  mettaPanelMotion(search, {
    panel,
    lead: () => [panel.querySelector('.metta-qs-title'), bar],
    rest: () => (form ? Array.from(form.children).filter((el) => el !== bar) : []),
    isOpen: () => search.classList.contains('active'),
    isShown: () => search.classList.contains('quick-search--visible'),
    reveal: {
      on: () => true,
      // Phones only: on desktop the drawer slides in on its CSS transition.
      open: () => mettaMenuPhone.matches && [{ clipPath: sheet() }, { clipPath: 'inset(0px)' }],
      close: () => mettaMenuPhone.matches && [
        { clipPath: 'inset(0px)', opacity: 1, transform: 'none' },
        { clipPath: sheet(), opacity: 1, transform: 'none' },
      ],
    },
  });
})();

(() => {
  const wrapper = document.querySelector('.quick-cart__wrapper');
  const panel = wrapper && wrapper.querySelector('.quick-cart__container');
  if (!panel || !Element.prototype.animate) return;
  const header = () => panel.querySelector('.quick-cart__header');
  mettaPanelMotion(wrapper, {
    panel,
    lead: () => [header()],
    rest: () => Array.from(panel.children).filter((el) => el !== header()),
    isOpen: () => wrapper.classList.contains('active'),
    isShown: () => wrapper.classList.contains('active'),
    reveal: { on: () => true },
  });
})();

/* Groups (Íþróttaföt, Lífsstíll, Versla eftir lit…): native <details>, one
   open at a time. data-open folds the + to − at the click, not when the
   fold ends. */
(() => {
  const groups = Array.from(document.querySelectorAll('.metta-dnav__group'));
  if (!groups.length) return;
  const runs = new Map();

  const toggle = (group, opening) => {
    const body = group.querySelector('summary').nextElementSibling;
    if (!body) return;
    (runs.get(group) || []).forEach((a) => a.cancel());
    group.toggleAttribute('data-open', opening);
    if (mettaMenuStill.matches || !Element.prototype.animate) {
      group.open = opening;
      return;
    }
    if (opening) group.open = true;
    body.style.overflow = 'hidden';
    const full = body.scrollHeight;
    const slid = { transform: 'translateX(-20px)', opacity: 0 };
    const rest = { transform: 'none', opacity: 1 };
    const set = opening
      ? [
          body.animate([{ height: '0px' }, { height: `${full}px` }], { duration: 200, easing: mettaMenuEase.out4 }),
          body.animate([slid, rest], { duration: 100, delay: 200, easing: mettaMenuEase.out3, fill: 'backwards' }),
        ]
      : [
          body.animate([rest, slid], { duration: 150, easing: mettaMenuEase.in3, fill: 'forwards' }),
          body.animate([{ height: `${full}px` }, { height: '0px' }], { duration: 200, delay: 150, easing: mettaMenuEase.in3, fill: 'forwards' }),
        ];
    runs.set(group, set);
    set[set.length - 1].onfinish = () => {
      if (!opening) group.open = false;
      body.style.overflow = '';
      set.forEach((a) => a.cancel());
      runs.delete(group);
    };
  };

  groups.forEach((group) => {
    group.querySelector('summary').addEventListener('click', (event) => {
      event.preventDefault();
      const opening = !group.hasAttribute('data-open');
      if (opening) {
        groups.forEach((other) => {
          if (other !== group && other.hasAttribute('data-open')) toggle(other, false);
        });
      }
      toggle(group, opening);
    });
  });
})();

/* Desktop menu photos load on the first hover or focus on the bar (19 Oct
   2026). As lazy images in a hidden menu they started only once the menu
   opened, so the photo faded in after the links. */
(() => {
  const bar = document.querySelector('.header');
  if (!bar) return;
  const warm = () => {
    bar.removeEventListener('pointerenter', warm);
    bar.removeEventListener('focusin', warm);
    document.querySelectorAll('.metta-meganav img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
  };
  bar.addEventListener('pointerenter', warm);
  bar.addEventListener('focusin', warm);
})();

/* Cart count badge (metta-header.css): theme.js writes the bare item count
   into it after every cart change. Hidden at 0, as Oner and SKIMS; "99+"
   past 99, so the pill stays two digits wide at most. */
(() => {
  const fit = (count) => {
    if (count.textContent.trim() === '99+') return;
    const n = parseInt(count.textContent, 10) || 0;
    count.toggleAttribute('data-empty', n === 0);
    const text = n > 99 ? '99+' : String(n);
    if (count.textContent !== text) count.textContent = text;
  };
  document.querySelectorAll('[data-js-cart-count]').forEach((count) => {
    fit(count);
    new MutationObserver(() => fit(count)).observe(count, { childList: true, characterData: true, subtree: true });
  });
})();
